import React from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { FaHandsHelping, FaBullseye, FaGraduationCap, FaHeartbeat, FaShieldAlt, FaHandHoldingHeart, FaUsers, FaSeedling, FaHeart, FaLightbulb, FaUserFriends } from 'react-icons/fa';
import './AboutPage.css';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const slideInFromBottom = {
    hidden: { opacity: 0, y: 100 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const AboutPage = ({ onNewsletterClick }) => {
    // Placeholder data - this should ideally come from a CMS or a dedicated data file
    const foundationInfo = {
        founded: '2016',
        operatesIn: 'Varanasi, Uttar Pradesh',
        address: 'Varanasi, Uttar Pradesh',
        focusAreas: ['Children', 'Education & Literacy', 'Health', 'Women Empowerment'],
        mission: "To provide proper education to every under-privileged child of India and make them literate and increase their decision-making capacity.",
        vision: "A society where every child has access to quality education and the opportunity to differentiate between what is wrong and what is right.",
        story: "Ummeed Ki Kiran Foundation Trust is a non-governmental organization founded in 2016 by service-minded and dedicated youth of Varanasi in the state of Uttar Pradesh. The team of UKKFT started the organisation with a view to make society a better place and work for the people who are in need. The NGO is registered under the Indian Trust Act and focuses on providing education to slum children and making them capable of differentiating between what is wrong and what is right.",
        values: [
            { icon: <FaHeart />, title: 'Compassion', text: 'We approach our work with empathy and a deep understanding of the needs of those we serve.' },
            { icon: <FaSeedling />, title: 'Integrity', text: 'We operate with transparency and accountability in all our endeavors.' },
            { icon: <FaLightbulb />, title: 'Empowerment', text: 'We strive to empower individuals and communities to create lasting change.' },
            { icon: <FaUserFriends />, title: 'Collaboration', text: 'We believe in the power of partnership to achieve greater impact.' }
        ],
        fieldsOfWork: [
            { icon: <FaGraduationCap />, title: 'Education', text: 'Ensuring quality education access for all children' },
            { icon: <FaHeartbeat />, title: 'Health & Nutrition', text: 'Promoting child health and nutritional wellbeing' },
            { icon: <FaShieldAlt />, title: 'Child Protection', text: 'Safeguarding children from harm and exploitation' },
            { icon: <FaHandHoldingHeart />, title: 'Humanitarian Aid', text: 'Providing emergency support during crises' },
            { icon: <FaUsers />, title: 'Social Inclusion', text: 'Ensuring no child is left behind due to social barriers' },
            { icon: <FaHandsHelping />, title: 'Community Resilience', text: 'Building stronger communities to support children' }
        ],
        faqs: [
            {
                question: 'How can I donate to support your cause?',
                answer: 'You can donate through our website, by bank transfer, or by contacting our donor support team. All donations are tax-exempted under relevant tax laws.'
            },
            {
                question: 'Is my donation information secure?',
                answer: 'We take utmost precautions with your data. We never share your information and do not store sensitive information like credit card or bank details.'
            },
            {
                question: 'How are my donations utilized?',
                answer: 'Your donations directly support our programs for children, including education, health, nutrition, and protection initiatives. We allocate resources to areas that need funds the most.'
            }
        ]
    };

    return (
        <div className="about-page">
            {/* Hero Section */}
            <motion.section
                className="about-hero-section"
                initial="hidden"
                animate="visible"
                viewport={{ once: true }}
            >
                <div className="hero-background">
                    <img src="/images/IMG-20250605-WA0396.jpg" alt="Ummeed Ki Kiran Foundation Trust About Us" />
                    <div className="hero-overlay"></div>
                </div>
                <motion.div className="hero-content" variants={staggerContainer}>
                    <div className="hero-text">
                        <motion.h1 className="hero-title" variants={fadeInUp}>
                            About Ummeed Ki Kiran Foundation Trust
                        </motion.h1>
                        <motion.p className="hero-subtitle" variants={fadeInUp}>
                            Empowering children and transforming lives in Varanasi since 2016 through education, health, and holistic development
                        </motion.p>
                        <motion.div className="hero-highlights" variants={staggerContainer}>
                            <motion.div className="highlight-item" variants={scaleIn}>
                                <FaGraduationCap className="highlight-icon" />
                                <span>Education First</span>
                            </motion.div>
                            <motion.div className="highlight-item" variants={scaleIn}>
                                <FaHeartbeat className="highlight-icon" />
                                <span>Health & Wellness</span>
                            </motion.div>
                            <motion.div className="highlight-item" variants={scaleIn}>
                                <FaHandsHelping className="highlight-icon" />
                                <span>Community Focus</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.div>
            </motion.section>

            {/* Focus Areas Section */}
            <motion.section
                className="focus-areas-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.div className="focus-areas-grid" variants={staggerContainer}>
                        <motion.div className="focus-area-item" variants={slideInFromBottom}>
                            <div className="focus-icon">
                                <FaGraduationCap />
                            </div>
                            <h3>Quality Education</h3>
                            <p>Providing comprehensive educational programs to underprivileged children in Varanasi slums</p>
                        </motion.div>
                        <motion.div className="focus-area-item" variants={slideInFromBottom}>
                            <div className="focus-icon">
                                <FaHeartbeat />
                            </div>
                            <h3>Healthcare Access</h3>
                            <p>Ensuring health and wellness through free medical camps and awareness programs</p>
                        </motion.div>
                        <motion.div className="focus-area-item" variants={slideInFromBottom}>
                            <div className="focus-icon">
                                <FaHandsHelping />
                            </div>
                            <h3>Community Development</h3>
                            <p>Building stronger communities through holistic development and empowerment programs</p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Main Overview Section */}
            <motion.section
                className="section overview-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        UMMEED KI KIRAN, BHAVISHYA KI KIRAN!
                    </motion.h2>
                    <motion.div className="overview-content" variants={staggerContainer}>
                        <motion.p variants={fadeInUp}>
                            At Ummeed ki kiran foundation Trust, nurturing children is nurturing the promise of India. Since {foundationInfo.founded}, we have made it our mission to transform childhood landscapes in {foundationInfo.operatesIn} through compassion, care and relentless efforts. We are steadfast in our resolve to help children build a Secure Childhood and thus a Secure Future.
                        </motion.p>
                        <motion.p variants={fadeInUp}>
                            Our approach is HOLISTIC as we address numerous aspects of childhood: access to health and nutrition, quality education, protection from harm, supporting with psychosocial needs and driving a well-rounded development of children. Protection from exploitation and access to equal opportunities help children thrive as architects of the nation's future.
                        </motion.p>
                        <motion.p variants={fadeInUp}>
                            We pledge to make every impact holistic for children - but we cannot walk alone, because shaping young lives cannot happen in isolation. It calls for unified effort from communities, government and supporters. Collaborating with on-ground workers, policymakers and donors, we build an ecosystem where children can flourish.
                        </motion.p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Our Fields of Work Section */}
            <motion.section
                className="section fields-section bg-light-alt"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        OUR FIELDS OF WORK
                    </motion.h2>
                    <motion.div className="fields-grid" variants={staggerContainer}>
                        {foundationInfo.fieldsOfWork.map((field, index) => (
                            <motion.div
                                key={index}
                                className="field-card"
                                variants={scaleIn}
                                whileHover={{
                                    y: -10,
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div className="field-icon">{field.icon}</div>
                                <h3 className="field-title">{field.title}</h3>
                                <p className="field-text">{field.text}</p>
                                {/* Commented out - no specific page available for field details */}
                                {/* <motion.a
                                    href="#"
                                    className="field-link"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Know More
                                </motion.a> */}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Education Section with Image */}
            <motion.section
                className="section focus-area-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <div className="section-content-split">
                        <motion.div className="split-image-container" variants={fadeInLeft}>
                            <img src="/images/IMG-20250605-WA0411.jpg" alt="Ummeed Ki Kiran Foundation Trust Education Program" className="split-image" />
                        </motion.div>
                        <motion.div className="split-text" variants={fadeInRight}>
                            <h6 className="focus-area-subtitle">SHIKSHA ki RAKSHA, BHAVISHYA ki RAKSHA!</h6>
                            <h3 className="focus-area-title">Education</h3>
                            <p>
                                From early childhood to adolescence, quality education unlocks human potential. Ummeed ki kiran foundation Trust champions the cause of India's underserved since {foundationInfo.founded}, aligning inclusive learning with national ethos. Safe classrooms, girls' participation, digital access - our interventions remove barriers spanning from urban slums to rural communities.
                            </p>
                            {/* Commented out - no specific education page available */}
                            {/* <motion.a
                                href="#"
                                className="know-more-button"
                                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)" }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                            >
                                Know More
                            </motion.a> */}
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Health Section with Image */}
            <motion.section
                className="section focus-area-section bg-light-alt"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <div className="section-content-split reverse">
                        <motion.div className="split-text" variants={fadeInLeft}>
                            <h6 className="focus-area-subtitle">AROGYA ki RAKSHA, BHAVISHYA ki RAKSHA!</h6>
                            <h3 className="focus-area-title">Health & Nutrition</h3>
                            <p>
                                Ummeed ki kiran foundation Trust aims to guarantee healthcare and nutrition for children across {foundationInfo.operatesIn}. Through awareness drives, health system strengthening, and tackling nutrition issues, we empower underserved communities through key partnerships with local health departments.
                            </p>
                            {/* Commented out - no specific health page available */}
                            {/* <motion.a
                                href="#"
                                className="know-more-button"
                                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)" }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                            >
                                Know More
                            </motion.a> */}
                        </motion.div>
                        <motion.div className="split-image-container" variants={fadeInRight}>
                            <img src="/images/IMG-20250605-WA0402.jpg" alt="Ummeed Ki Kiran Foundation Trust Health Care Activities" className="split-image" />
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Mission and Vision Section */}
            <motion.section
                className="section mission-vision-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        IN CLOSE COORDINATION:<br />WORKING AND ENGAGING WITH COMMUNITIES TO DRIVE CHANGE
                    </motion.h2>
                    <motion.div className="mission-vision-grid" variants={staggerContainer}>
                        <motion.div className="mission-item" variants={fadeInLeft}>
                            <FaBullseye className="mission-vision-icon" />
                            <h3 className="sub-heading">Our Mission</h3>
                            <p>{foundationInfo.mission}</p>
                        </motion.div>
                        <motion.div className="vision-item" variants={fadeInRight}>
                            <FaHandsHelping className="mission-vision-icon" />
                            <h3 className="sub-heading">Our Vision</h3>
                            <p>{foundationInfo.vision}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Our Journey Section */}
            <motion.section
                className="section journey-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Our Journey
                    </motion.h2>
                    <motion.p className="section-tagline text-center" variants={fadeInUp}>
                        From a humble beginning to a beacon of hope for many.
                    </motion.p>
                    <motion.div className="journey-content" variants={fadeInUp}>
                        <p>{foundationInfo.story}</p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Our Values Section */}
            <motion.section
                className="section values-section bg-light-alt"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Our Core Values
                    </motion.h2>
                    <motion.p className="section-tagline text-center" variants={fadeInUp}>
                        The principles that guide every action we take.
                    </motion.p>
                    <motion.div className="values-grid" variants={staggerContainer}>
                        {foundationInfo.values.map((value, index) => (
                            <motion.div
                                key={index}
                                className="value-card"
                                variants={scaleIn}
                                whileHover={{
                                    y: -10,
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div className="value-icon">{value.icon}</div>
                                <h3 className="value-title">{value.title}</h3>
                                <p className="value-text">{value.text}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* FAQ Section */}
            <motion.section
                className="section faq-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Frequently Asked Questions
                    </motion.h2>
                    <motion.div className="faq-container" variants={staggerContainer}>
                        {foundationInfo.faqs.map((faq, index) => (
                            <motion.div
                                key={index}
                                className="faq-item"
                                variants={fadeInUp}
                                whileHover={{
                                    x: 10,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <h3 className="faq-question">{faq.question}</h3>
                                <p className="faq-answer">{faq.answer}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Newsletter Subscription Section */}
            <motion.section
                className="newsletter-subscription-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="newsletter-container">
                    <motion.div className="newsletter-content" variants={staggerContainer}>
                        <motion.h2 className="newsletter-title" variants={fadeInUp}>
                            Stay Connected With Our Mission
                        </motion.h2>
                        <motion.p className="newsletter-subtitle" variants={fadeInUp}>
                            Subscribe to our newsletter and be the first to know about our impact stories, new projects, and ways you can help transform lives.
                        </motion.p>
                        <motion.p className="newsletter-tagline" variants={fadeInUp}>
                            Join our community of changemakers today!
                        </motion.p>
                    </motion.div>

                    <motion.div className="newsletter-features" variants={scaleIn}>
                        <div className="newsletter-features-grid">
                            <motion.div className="newsletter-feature" variants={scaleIn}>
                                <div className="newsletter-feature-icon">📧</div>
                                <p className="newsletter-feature-text">Monthly Updates</p>
                            </motion.div>
                            <motion.div className="newsletter-feature" variants={scaleIn}>
                                <div className="newsletter-feature-icon">📖</div>
                                <p className="newsletter-feature-text">Impact Stories</p>
                            </motion.div>
                            <motion.div className="newsletter-feature" variants={scaleIn}>
                                <div className="newsletter-feature-icon">🎯</div>
                                <p className="newsletter-feature-text">Project Updates</p>
                            </motion.div>
                        </div>
                    </motion.div>

                    <motion.button
                        onClick={onNewsletterClick}
                        className="newsletter-subscribe-button"
                        variants={fadeInUp}
                        whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)" }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        📰 Subscribe to Newsletter
                    </motion.button>

                    <motion.p className="newsletter-disclaimer" variants={fadeInUp}>
                        ✅ No spam, just meaningful updates • ✅ Unsubscribe anytime • ✅ Your privacy is protected
                    </motion.p>
                </div>
            </motion.section>
        </div>
    );
};

export default AboutPage;